import React, { useState, useEffect } from 'react';
import { useAltHotkeys } from '../hooks/useAltHotkeys';
import InfoModal from './common/InfoModal';
import Toggle from './Toggle';
import api from '../services/api';
import { cachedGet } from '../utils/lookupCache';

interface Location {
  recno: number;
  lname: string;
  locationtype: string;
  ladd: string;
  ladd2: string;
  lcity: string;
  lstate: string;
  lzip: string;
}

interface LocationPreference {
  location_id: number;
  location_name: string;
  ban_start_date?: string;
  ban_end_date?: string;
}

interface DriverPreferenceModalProps {
  isOpen: boolean;
  onClose: () => void;
  driver: string;
  driverName: string;
  preferenceType: 'preferred' | 'not_preferred' | 'banned';
  editingBan?: LocationPreference | null;
  onSave: () => void;
}

const DriverPreferenceModal: React.FC<DriverPreferenceModalProps> = ({
  isOpen,
  onClose,
  driver,
  driverName,
  preferenceType,
  editingBan,
  onSave,
}) => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [locationsLoadFailed, setLocationsLoadFailed] = useState(false);
  const [selectedLocations, setSelectedLocations] = useState<number[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isPermanent, setIsPermanent] = useState(true);
  const [banEndDate, setBanEndDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const getTitle = () => {
    switch (preferenceType) {
      case 'preferred':
        return `Add Preferred Locations - ${driver} ${driverName}`;
      case 'not_preferred':
        return `Add Not-Preferred Locations - ${driver} ${driverName}`;
      case 'banned':
        return `Add Banned Locations - ${driver} ${driverName}`;
    }
  };

  const filteredLocations = locations.filter(loc =>
    loc.lname.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleLocation = (locationId: number) => {
    setSelectedLocations(prev =>
      prev.includes(locationId)
        ? prev.filter(id => id !== locationId)
        : [...prev, locationId]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      for (const locationId of selectedLocations) {
        const payload: any = {
          driver,
          location_id: locationId,
          preference_type: preferenceType,
        };

        if (preferenceType === 'banned') {
          if (!isPermanent && banEndDate) {
            payload.ban_start_date = new Date().toISOString().split('T')[0];
            payload.ban_end_date = banEndDate;
          } else {
            payload.ban_start_date = new Date().toISOString().split('T')[0];
            payload.ban_end_date = null;
          }
        }

        await api.post('/safety/driver-preferences', payload);
      }

      onSave();
      onClose();
      setSelectedLocations([]);
      setSearchQuery('');
      setIsPermanent(true);
      setBanEndDate('');
    } catch (error) {
      console.error('Error saving driver preferences:', error);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Initialize form state when modal opens - fetches locations and pre-populates
   * form from editingBan prop if editing an existing ban.
   * Note: This useEffect is intentional for modal forms that need to:
   * 1. Fetch data when opening
   * 2. Reset or populate form from props
   * A key prop alternative would require parent coordination for modal state.
   */
  useEffect(() => {
    if (isOpen) {
      const fetchLocations = async () => {
        setLoading(true);
        setLocationsLoadFailed(false);
        try {
          const response = await cachedGet<unknown[]>('/admin/locations');
          setLocations((response.data as never[]) || []);
        } catch (error) {
          console.error('Error fetching locations:', error);
          setLocationsLoadFailed(true);
        } finally {
          setLoading(false);
        }
      };

      fetchLocations();

      // Pre-populate form if editing an existing ban
      if (editingBan) {
        setSelectedLocations([editingBan.location_id]);
        if (editingBan.ban_end_date) {
          setIsPermanent(false);
          setBanEndDate(editingBan.ban_end_date);
        } else {
          setIsPermanent(true);
          setBanEndDate('');
        }
      } else {
        // Reset form for new entry
        setSelectedLocations([]);
        setIsPermanent(true);
        setBanEndDate('');
        setSearchQuery('');
      }
    }
  }, [isOpen, editingBan]);

  // Alt+S saves, Alt+C closes — both mirroring the footer buttons' saving guard.
  useAltHotkeys(
    { S: saving ? null : handleSave, C: saving ? null : onClose },
    undefined,
    { layer: isOpen },
  );

  return (
    <InfoModal
      isOpen={isOpen}
      onClose={onClose}
      title={getTitle()}
      maxWidth="2xl"
      body={
        <div className="space-y-4">
          {preferenceType === 'banned' && (
            <div className="space-y-3 pb-3 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-label">
                  {isPermanent ? 'Permanent Ban' : 'Temporary Ban'}
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted">Temporary</span>
                  <Toggle
                    checked={isPermanent}
                    onChange={setIsPermanent}
                    size="sm"
                  />
                  <span className="text-xs text-muted">Permanent</span>
                </div>
              </div>
              {!isPermanent && (
                <div>
                  <label className="block text-sm font-medium text-label mb-1">
                    Ban End Date
                  </label>
                  <input
                    type="date"
                    value={banEndDate}
                    onChange={(e) => setBanEndDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-label mb-2">
              Select Locations
            </label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search locations..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white mb-3"
            />
            <div className="border border-gray-300 dark:border-gray-600 rounded-lg max-h-96 overflow-y-auto">
              {loading ? (
                <div className="p-4 text-center text-muted">Loading locations...</div>
              ) : filteredLocations.length === 0 ? (
                <div className="p-4 text-center text-muted">
                  {locationsLoadFailed
                    ? 'Couldn\'t load locations — the request failed. Close and reopen to retry.'
                    : 'No locations found'}
                </div>
              ) : (
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredLocations.map((location) => (
                    <label
                      key={location.recno}
                      className="flex items-center p-3 hover:bg-highlight cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedLocations.includes(location.recno)}
                        onChange={() => toggleLocation(location.recno)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <div className="ml-3 flex-1">
                        <div className="text-sm font-medium text-heading">
                          {location.lname}
                        </div>
                        <div className="text-xs text-muted">
                          {location.locationtype && `${location.locationtype} • `}
                          {[location.ladd, location.ladd2].filter(Boolean).join(' ')}
                          {location.lcity && `, ${location.lcity}, ${location.lstate} ${location.lzip}`}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
            {selectedLocations.length > 0 && (
              <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                {selectedLocations.length} location{selectedLocations.length !== 1 ? 's' : ''} selected
              </div>
            )}
          </div>
        </div>
      }
      buttons={[
        {
          label: 'Cancel',
          onClick: onClose,
          variant: 'secondary'
        },
        {
          label: saving ? 'Saving...' : 'Save',
          onClick: handleSave,
          variant: 'primary',
          disabled: saving || selectedLocations.length === 0
        }
      ]}
    />
  );
};

export default DriverPreferenceModal;
